import React from 'react';
import PropTypes from 'prop-types';
import { fromGlobalId } from 'graphql-relay';
import { FormattedMessage, injectIntl } from 'react-intl';
import get from 'lodash/get';
import Grid from '../Grid';
import Button from '../Button';
import Avatar from '../Avatar';
import Icon from '../Icon';
import Modal from '../Modal';
import Loader from '../Loader';
import Message from '../Message';
import HighSchoolDetailsDisplay from '../../containers/HighSchoolDetailsDisplay';
import CollegeFairDetails from '../../containers/CollegeFairDetails';
import RepVisitsCalendarEventDetailsSideBar from '../../containers/RepVisitsCalendarEventDetailsSideBar';
import REPVISITS_LOCATION_TYPE from '../../utils/repvisits-location-types';
import SidebarWrapper from '../SidebarWrapper';
import SmartFormattedRelativeTime from '../SmartFormattedRelativeTime';

import * as constants from './constants';
import messages from './messages';
import './styles.css';

const styles = {
  DECLINED_CANCELLED: 'notification-declined-cancelled',
  CONFIRMED_SCHEDULED: 'notification-confirmed-scheduled',
  RESCHEDULED: 'notification-rescheduled',
};

export const DATE_FORMAT = {
  year: 'numeric',
  day: '2-digit',
  month: 'short',
  weekday: 'long',
};

export class ActivityNotification extends React.PureComponent {
  static propTypes = {
    notification: PropTypes.object.isRequired,
    currentUserId: PropTypes.string.isRequired,
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
      formatDate: PropTypes.func.isRequired,
    }).isRequired,
    displaySidebarComponent: PropTypes.func.isRequired,
    closeSidebar: PropTypes.func.isRequired,
    dismissNotification: PropTypes.func,
    addVisitUrl: PropTypes.func,
  };

  state = {
    highSchoolId: null,
    loading: false,
    error: null,
  }

  onDismiss = () => {
    this.setState({ loading: true });
    this.props.dismissNotification(this.props.notification.id)
      .then(() => this.setState({ loading: false }))
      .catch(() => this.setState({ error: true, loading: false }));
  }

  onAddVisitUrl = () => {
    this.props.displaySidebarComponent(this.props.notification, true, this.props.notification.id);
  }

  isVirtualNotification = () => [
    constants.VISIT_TYPE_CHANGED_TO_IN_PERSON,
    constants.VISIT_TYPE_CHANGED_TO_VIRTUAL,
  ].includes(this.props.notification.notificationType);

  prepareRender = () => {
    const { notification, intl } = this.props;
    const label = this.getNotificationLabel(notification);
    const date = intl.formatDate(`${notification.event.date}T12:00:00`, DATE_FORMAT);
    const virtualStyle = this.isVirtualNotification() ? 'with-button' : '';
    const highSchoolId = notification.highSchool.id && fromGlobalId(notification.highSchool.id).id;
    const highSchoolTitle = notification.highSchool.title;
    const actorName = notification.actor ? notification.actor.name : highSchoolTitle;
    const actorPicture = notification.actor ? notification.actor.field_profile_picture_file_url : null;
    return {
      notification,
      intl,
      label,
      date,
      virtualStyle,
      highSchoolId,
      highSchoolTitle,
      actorName,
      actorPicture,
    };
  }

  getNotificationLabel = () => {
    const { intl, notification } = this.props;
    const a = intl.formatMessage(messages.a);
    const an = intl.formatMessage(messages.an);
    const thisVisit = intl.formatMessage(messages.this);
    const your = intl.formatMessage(messages.your);
    const forYou = intl.formatMessage(messages.forYou);
    const request = intl.formatMessage(messages.request);
    const appointment = intl.formatMessage(messages.appointment);
    const inPerson = intl.formatMessage(messages.inPerson);
    const virtual = intl.formatMessage(messages.virtual);
    const isCancelled = get(notification, 'notificationType') === constants.VISIT_CANCELLED;
    const cancelledVisitType = isCancelled && get(notification, 'canceledRsvpChosenVisitLocationType');
    const isVirtual = (cancelledVisitType || get(notification, 'event.chosenVisitLocationType')) === REPVISITS_LOCATION_TYPE.VIRTUAL;
    const visitTypeText = isVirtual ? virtual : inPerson;

    switch (notification.notificationType) {
      case constants.VISIT_REQUEST_APPROVED:
        return {
          action: intl.formatMessage(messages.confirmed),
          to: your,
          visitType: visitTypeText,
          target: `${intl.formatMessage(messages.visit)} ${request}`,
          style: styles.CONFIRMED_SCHEDULED,
          detailsLink: true,
        };
      case constants.VISIT_REQUEST_DECLINED:
        return {
          action: intl.formatMessage(messages.declined),
          to: your,
          visitType: visitTypeText,
          target: `${intl.formatMessage(messages.visit)} ${request}`,
          style: styles.DECLINED_CANCELLED,
        };
      case constants.VISIT_RESCHEDULED:
        return {
          action: intl.formatMessage(messages.rescheduled),
          to: your,
          visitType: visitTypeText,
          target: `${intl.formatMessage(messages.visit)} ${request}`,
          style: styles.RESCHEDULED,
          detailsLink: true,
        };
      case constants.VISIT_CANCELLED:
        return {
          action: intl.formatMessage(messages.cancelled),
          to: your,
          visitType: visitTypeText,
          target: `${intl.formatMessage(messages.visit)} ${request}`,
          style: styles.DECLINED_CANCELLED,
        };
      case constants.VISIT_MANUALLY_SCHEDULED:
        return {
          action: intl.formatMessage(messages.scheduled),
          to: isVirtual ? a : an,
          visitType: visitTypeText,
          target: `${intl.formatMessage(messages.visit)} ${forYou}`,
          style: styles.CONFIRMED_SCHEDULED,
          detailsLink: true,
        };
      case constants.VISIT_REASSIGNED:
        return {
          action: intl.formatMessage(messages.reassigned),
          to: thisVisit,
          visitType: visitTypeText,
          target: `${intl.formatMessage(messages.visit)}`,
          style: styles.RESCHEDULED,
          detailsLink: true,
        };
      case constants.FAIR_REQUEST_APPROVED:
        return {
          action: intl.formatMessage(messages.confirmed),
          to: your,
          target: `${intl.formatMessage(messages.collegeFair)} ${appointment}`,
          style: styles.CONFIRMED_SCHEDULED,
          detailsLink: true,
        };
      case constants.FAIR_REQUEST_DECLINED:
        return {
          action: intl.formatMessage(messages.declined),
          to: your,
          target: `${intl.formatMessage(messages.collegeFair)} ${appointment} ${request}`,
          style: styles.DECLINED_CANCELLED,
        };
      case constants.FAIR_RESCHEDULED:
        return {
          action: intl.formatMessage(messages.rescheduled),
          to: your,
          target: `${intl.formatMessage(messages.collegeFair)} ${appointment}`,
          style: styles.RESCHEDULED,
          detailsLink: true,
        };
      case constants.FAIR_CANCELLED:
        return {
          action: intl.formatMessage(messages.cancelled),
          to: your,
          target: `${intl.formatMessage(messages.collegeFair)} ${appointment}`,
          style: styles.DECLINED_CANCELLED,
        };
      case constants.FAIR_ATTENDEE_CANCELLED:
        return {
          action: intl.formatMessage(messages.cancelled),
          to: a,
          target: `${intl.formatMessage(messages.collegeFair)} ${appointment}`,
          style: styles.DECLINED_CANCELLED,
        };
      case constants.FAIR_REASSIGNED:
        return {
          action: intl.formatMessage(messages.reassigned),
          to: a,
          target: `${intl.formatMessage(messages.collegeFair)} ${appointment}`,
          style: styles.RESCHEDULED,
          detailsLink: true,
        };
      case constants.VISIT_TYPE_CHANGED_TO_VIRTUAL:
        return {
          action: intl.formatMessage(messages.changeVisit),
          detailsLink: true,
          style: '',
        };
      case constants.VISIT_TYPE_CHANGED_TO_IN_PERSON:
        return {
          action: intl.formatMessage(messages.changeVisit),
          detailsLink: true,
          style: '',
        };
    }
    return { style: '' };
  };

  displayEventDetails = () => {
    const { notification, currentUserId } = this.props;
    if (this.isVirtualNotification()) {
      this.props.displaySidebarComponent(notification);
      return;
    }
    let Component;
    const props = {};
    let actorId = get(notification, 'actor.uuid');
    if (actorId) {
      actorId = fromGlobalId(actorId).id;
    }
    if (currentUserId !== actorId) {
      // only send contact to details if it is HS contact
      props.contact = notification.actor;
    }
    if (notification.event.dayId) {
      Component = RepVisitsCalendarEventDetailsSideBar;
      props.event = notification.event;
      props.notificationId = notification.id;
      props.onSave = this.props.closeSidebar;
    } else {
      Component = CollegeFairDetails;
      props.id = notification.event.collegeFairId;
    }

    this.props.displaySidebarComponent(
      <SidebarWrapper.Bar>
        <Component {...props} />
      </SidebarWrapper.Bar>,
    );
  };

  openHighSchoolDetails = (highSchoolId) => this.setState({ highSchoolId });

  closeHighSchoolDetails = () => this.setState({ highSchoolId: null });

  renderHighSchoolDetails = () => (
    <Modal
      open={this.state.highSchoolId}
      onClose={this.closeHighSchoolDetails}
      basic
      closeIcon={<Icon data-cy="close-modal" name="close" styleName="details-modal-close" />}
      styleName="details-modal"
    >
      <HighSchoolDetailsDisplay id={this.state.highSchoolId} />
    </Modal>
  )

  renderTitle = (highSchoolTitle, highSchoolId, actorName, label) => {
    if (this.isVirtualNotification()) {
      if (this.props.notification.notificationType === constants.VISIT_TYPE_CHANGED_TO_VIRTUAL) {
        return (
          <React.Fragment>
            <button
              data-cy="high-school-details-button"
              type="button"
              styleName="highschool-link"
              onClick={() => this.openHighSchoolDetails(highSchoolId)}
            >
              {highSchoolTitle}
            </button>
            {label.action}
            {this.props.intl.formatMessage(messages.from2)}
            <strong>{this.props.intl.formatMessage(messages.inPersonVisit)}</strong>
            {this.props.intl.formatMessage(messages.to1)}
            <strong>{this.props.intl.formatMessage(messages.virtualVisit)}</strong>
            {this.props.intl.formatMessage(messages.dots)}
          </React.Fragment>
        );
      }
      return (
        <React.Fragment>
          <button
            data-cy="high-school-details-button"
            type="button"
            styleName="highschool-link"
            onClick={() => this.openHighSchoolDetails(highSchoolId)}
          >
            {highSchoolTitle}
          </button>
          {label.action}
          {this.props.intl.formatMessage(messages.from1)}
          <strong>{this.props.intl.formatMessage(messages.virtualVisit)}</strong>
          {this.props.intl.formatMessage(messages.to2)}
          <strong>{this.props.intl.formatMessage(messages.inPersonVisit)}</strong>
          {this.props.intl.formatMessage(messages.dots)}
        </React.Fragment>
      );
    }
    return (
      <React.Fragment>
        <span>{actorName}</span> <strong>{label.action}</strong> {label.to}{label.visitType ? <strong> {label.visitType}</strong> : ''} {label.target}
      </React.Fragment>
    );
  }

  renderDismissButton = () => {
    const { VISIT_TYPE_CHANGED_TO_IN_PERSON, VISIT_TYPE_CHANGED_TO_VIRTUAL } = constants;
    const { notification: { event, notificationType } } = this.props;
    const inPersonNotification = notificationType === VISIT_TYPE_CHANGED_TO_IN_PERSON;
    const virtualNotificationCreator = notificationType === VISIT_TYPE_CHANGED_TO_VIRTUAL && event.visitCreator === 'HIGH_SCHOOL';

    if (!inPersonNotification && !virtualNotificationCreator) return null;
    return (
      <React.Fragment>
        {this.state.error && <Message error header={this.props.intl.formatMessage(messages.errorDismiss)} content={this.state.error} />}
        <Loader inline active={this.state.loading} />
        <Button
          data-cy="dismiss-notification-button"
          styleName="notification-button"
          type="button"
          primary
          onClick={this.onDismiss}
        >
          <FormattedMessage {...messages.dismiss} />
        </Button>
      </React.Fragment>
    );
  }

  renderAddUrlButton = () => {
    const { VISIT_TYPE_CHANGED_TO_VIRTUAL } = constants;
    const { notification: { event, notificationType } } = this.props;
    const virtualNotification = notificationType === VISIT_TYPE_CHANGED_TO_VIRTUAL;
    const virtualNotificationCreator = notificationType === VISIT_TYPE_CHANGED_TO_VIRTUAL && event.visitCreator === 'HIGH_SCHOOL';

    if (!virtualNotification || virtualNotificationCreator) return null;
    return (
      <Button
        data-cy="add-url-button"
        styleName="notification-button"
        type="button"
        primary
        onClick={this.onAddVisitUrl}
      >
        <FormattedMessage {...messages.addUrl} />
      </Button>
    );
  }

  render() {
    const {
      notification,
      intl,
      label,
      date,
      virtualStyle,
      highSchoolId,
      highSchoolTitle,
      actorName,
      actorPicture,
    } = this.prepareRender();
    return (
      <li styleName="notification-grid" data-cy="repvisits-schools-activity-list" className="ui grid">
        {this.renderHighSchoolDetails()}
        <div styleName={`notification-info ${virtualStyle}`}>
          <Grid.Row styleName={`notification-row ${label.style}`}>
            <Grid.Column width={10}>
              <div styleName="notification-age"><SmartFormattedRelativeTime value={notification.date} /></div>
              <div data-cy="repvisits-schools-activity-status">
                {this.renderTitle(highSchoolTitle, highSchoolId, actorName, label)}
              </div>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row styleName="notification-row">
            <Grid.Column width={2} styleName="avatar-container">
              <Avatar
                big
                icon="university"
              />
              <div styleName="actor-avatar">
                <Avatar
                  big
                  icon="user"
                  image={actorPicture}
                />
              </div>
            </Grid.Column>
            <Grid.Column width={10}>
              <div styleName="grey highschool-name"><span>{highSchoolTitle}</span></div>
              <div styleName="grey">{`${notification.highSchool.address.field_hs_elna_city},
                                  ${notification.highSchool.address.field_hs_elna_state_latest}`}
              </div>
              {!notification.rescheduleInfo && (
                <div styleName="grey">
                  <FormattedMessage
                    {...messages.dateTime}
                    values={{
                      date,
                      time: notification.event.startTime,
                    }}
                  />
                </div>
              )}
              {notification.rescheduleInfo && (
                <div styleName="grey">
                  <del>
                    <FormattedMessage
                      {...messages.dateTime}
                      values={{
                        date: intl.formatDate(`${notification.rescheduleInfo.date}T12:00:00`, DATE_FORMAT),
                        time: notification.rescheduleInfo.startTime,
                      }}
                    />
                  </del>&nbsp;
                  <Icon name="long arrow alternate right" />
                  <FormattedMessage
                    {...messages.dateTime}
                    values={{
                      date,
                      time: notification.event.startTime,
                    }}
                  />
                </div>
              )}
              {notification.reassignInfo && (
                <div styleName="grey">
                  <del>
                    {notification.reassignInfo.fromContact.name}
                  </del>&nbsp;
                  <Icon name="long arrow alternate right" />
                  {notification.reassignInfo.toContact.name}
                </div>
              )}
              {label.detailsLink && (
                <Button data-cy="show-details-button" size="small" basic onClick={this.displayEventDetails}>
                  <FormattedMessage {...messages.viewFullDetails} />
                </Button>
              )}
              {!label.detailsLink && <br />}
            </Grid.Column>
          </Grid.Row>
          <Grid.Row styleName="notification-row">
            <Grid.Column width={14}>
              {notification.message && (
                <div styleName="grey included-message">
                  <span><FormattedMessage {...messages.theyIncludedAMessage} /></span> {notification.message}
                </div>
              )}
            </Grid.Column>
          </Grid.Row>
        </div>
        <div styleName="notification-action">
          {this.renderDismissButton()}
          {this.renderAddUrlButton()}
        </div>
      </li>
    );
  }
}

export const intlActivityNotification = injectIntl(ActivityNotification);
export default intlActivityNotification;
